import { ChangeDetectorRef, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material';
import { ActivatedRoute } from '@angular/router';
import { interval, timer } from 'rxjs';
import { switchMap, takeUntil, takeWhile } from 'rxjs/operators';
import { mapApiProductNameToDisplayName } from '../../../../core/_config/product.constants';
import { InventoryHttpService } from '../../../../core/inventory';
import { AugerPurgeHttpService } from '../../../../core/settings/_services/auger-purge-http.service';
import { AUGER_PURGE_FIELDS, AUGER_PURGE_MESSAGES, augerPurgeStatusLabel, cleaningCycleHintForInput, describeCommandPayload, draftFromConfig, evaluateDraft, formatEnabled, isPendingAugerPurgeStatus, splitServerErrors, unreportedFieldTooltip } from '../../../../core/settings/_services/auger-purge.util';
import { MixpanelService } from '../../../../services/mixpanel.service';
import { MachineRouteService } from '../machine-route.service';
import { AugerPurgeConfirmDialogComponent } from './auger-purge-confirm-dialog.component';
var AugerPurgeComponent = /** @class */ (function () {
    function AugerPurgeComponent(route, machineRouteService, http, inventoryHttp, dialog, cdr, mixpanelService) {
        this.route = route;
        this.machineRouteService = machineRouteService;
        this.http = http;
        this.inventoryHttp = inventoryHttp;
        this.dialog = dialog;
        this.cdr = cdr;
        this.mixpanelService = mixpanelService;
        this.fields = AUGER_PURGE_FIELDS;
        this.unknownTooltip = AUGER_PURGE_MESSAGES.unknownTooltip;
        this.basisDefaultTooltip = AUGER_PURGE_MESSAGES.basisDefaultTooltip;
        this.intervalDefaultTooltip = AUGER_PURGE_MESSAGES.intervalDefaultTooltip;
        this.unreportedTooltip = unreportedFieldTooltip;
        this.statusLabel = augerPurgeStatusLabel;
        this.describeChange = describeCommandPayload;
        this.purgeOptions = [
            { label: 'On', value: 'on' },
            { label: 'Off', value: 'off' }
        ];
        this.machineId = '';
        this.config = null;
        this.draft = null;
        this.isLoading = false;
        this.loadError = '';
        this.phase = 'idle';
        this.statusMessage = '';
        this.formError = '';
        this.serverFieldErrors = {};
        this.productsBySlot = {};
        this.destroyed = false;
        this.pollSubscription = null;
        this.lastPatch = null;
        this.lastSummary = [];
    }
    AugerPurgeComponent.prototype.ngOnInit = function () {
        var machine = this.machineRouteService.getMachine(this.route);
        if (!machine) {
            this.loadError = 'Open this page from a machine.';
            return;
        }
        this.machineId = String(machine.machine_id || '');
        this.mixpanelService.track(this.mixpanelService.PAGE_VIEW, {
            machine_id: this.machineId,
            machine_name: machine.machine_name,
            machine_serial_no: machine.serial_no
        });
        this.loadConfig(false);
        this.loadProducts();
    };
    AugerPurgeComponent.prototype.ngOnDestroy = function () {
        this.destroyed = true;
        this.stopWatching();
    };
    Object.defineProperty(AugerPurgeComponent.prototype, "formLocked", {
        get: function () {
            return this.phase === 'sending' || this.phase === 'waiting';
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(AugerPurgeComponent.prototype, "canSend", {
        get: function () {
            if (!this.config || !this.draft || this.formLocked || this.formError || Object.keys(this.serverFieldErrors).length) {
                return false;
            }
            var evaluation = evaluateDraft(this.config, this.draft);
            return !evaluation.errors.length && !!evaluation.patch;
        },
        enumerable: true,
        configurable: true
    });
    AugerPurgeComponent.prototype.productName = function (slot) {
        return this.productsBySlot[slot] || '';
    };
    AugerPurgeComponent.prototype.reportedCanister = function (slot) {
        if (!this.config) {
            return null;
        }
        return (this.config.canisters || []).filter(function (canister) { return canister.slot === slot; })[0] || null;
    };
    AugerPurgeComponent.prototype.fieldError = function (slot, key) {
        var field = slot + '.' + key;
        if (this.serverFieldErrors[field]) {
            return this.serverFieldErrors[field];
        }
        if (!this.config || !this.draft) {
            return '';
        }
        var match = evaluateDraft(this.config, this.draft).errors.filter(function (error) { return error.field === field; })[0];
        return match ? match.message : '';
    };
    AugerPurgeComponent.prototype.enabledError = function () {
        if (this.serverFieldErrors.enabled) {
            return this.serverFieldErrors.enabled;
        }
        if (!this.config || !this.draft) {
            return '';
        }
        var match = evaluateDraft(this.config, this.draft).errors.filter(function (error) { return error.field === 'enabled'; })[0];
        return match ? match.message : '';
    };
    AugerPurgeComponent.prototype.unknownField = function (slot, key) {
        var canister = this.reportedCanister(slot);
        if (!canister) {
            return true;
        }
        return canister[key] === null || canister[key] === undefined;
    };
    AugerPurgeComponent.prototype.fieldTooltip = function (row, key) {
        if (this.unknownField(row.slot, key) && !row[key]) {
            return this.unreportedTooltip(key);
        }
        if (key === 'frequency_cycles') {
            return this.cycleHint(row);
        }
        return '';
    };
    AugerPurgeComponent.prototype.cycleHint = function (row) {
        if (!this.config) {
            return '';
        }
        var canister = this.reportedCanister(row.slot);
        var interval = canister ? this.reportedInterval(canister.cleaning_interval) : null;
        return cleaningCycleHintForInput(this.config.cleaning_basis, interval, row.frequency_cycles) || '';
    };
    AugerPurgeComponent.prototype.intervalText = function (slot) {
        var canister = this.reportedCanister(slot);
        var value = canister ? this.reportedInterval(canister.cleaning_interval) : null;
        if (value === null) {
            return '';
        }
        var basis = this.config && this.config.cleaning_basis;
        if (basis === 'drinks') {
            return value + (value === 1 ? ' drink' : ' drinks');
        }
        if (basis === 'minutes') {
            return value + (value === 1 ? ' minute' : ' minutes');
        }
        return String(value);
    };
    AugerPurgeComponent.prototype.isFrequencyOff = function (row) {
        return String(row.frequency_cycles || '').trim() === '0';
    };
    AugerPurgeComponent.prototype.enabledLabel = function () {
        return formatEnabled(this.config ? this.config.enabled : null);
    };
    AugerPurgeComponent.prototype.basisLabel = function () {
        if (!this.config || !this.config.cleaning_basis) {
            return 'Unknown';
        }
        return this.config.cleaning_basis;
    };
    AugerPurgeComponent.prototype.onEnabledChange = function (value) {
        if (!this.draft) {
            return;
        }
        if (value === null && this.config && (this.config.enabled === true || this.config.enabled === false)) {
            this.draft.enabled = this.config.enabled ? 'on' : 'off';
        }
        else {
            this.draft.enabled = value;
        }
        this.clearServerErrors();
    };
    AugerPurgeComponent.prototype.onDraftChange = function () {
        this.clearServerErrors();
    };
    AugerPurgeComponent.prototype.onFieldInput = function (row, key, event) {
        var input = event.target;
        var digits = input.value.replace(/\D/g, '').replace(/^0+(?=\d)/, '');
        if (input.value !== digits) {
            input.value = digits;
        }
        row[key] = digits;
        this.onDraftChange();
    };
    AugerPurgeComponent.prototype.inputWidth = function (row, field) {
        var value = String(row[field.key] || '');
        if (!value) {
            return '7.5ch';
        }
        return value.length + 'ch';
    };
    AugerPurgeComponent.prototype.canStep = function (row, field, delta) {
        var raw = String(row[field.key] || '').trim();
        if (!raw) {
            return delta > 0;
        }
        var value = Number(raw);
        if (!Number.isFinite(value)) {
            return false;
        }
        return delta > 0 ? value < field.max : value > field.min;
    };
    AugerPurgeComponent.prototype.stepField = function (row, field, delta) {
        if (this.formLocked || !this.canStep(row, field, delta)) {
            return;
        }
        var raw = String(row[field.key] || '').trim();
        var step = field.step || 1;
        var next = raw ? Number(raw) + (delta * step) : Math.max(field.min, step);
        if (next < field.min) {
            next = field.min;
        }
        if (next > field.max) {
            next = field.max;
        }
        row[field.key] = String(next);
        this.onDraftChange();
    };
    AugerPurgeComponent.prototype.onSend = function () {
        if (!this.config || !this.draft || this.formLocked) {
            return;
        }
        var evaluation = evaluateDraft(this.config, this.draft);
        if (evaluation.errors.length || !evaluation.patch) {
            this.cdr.detectChanges();
            return;
        }
        this.confirmAndSend(evaluation);
    };
    AugerPurgeComponent.prototype.sendAgain = function () {
        if (!this.lastPatch || this.formLocked) {
            return;
        }
        this.confirmAndSend({
            patch: this.lastPatch,
            errors: [],
            summary: this.lastSummary
        });
    };
    AugerPurgeComponent.prototype.confirmAndSend = function (evaluation) {
        var _this = this;
        var dialogRef = this.dialog.open(AugerPurgeConfirmDialogComponent, {
            width: '480px',
            data: { summaryLines: evaluation.summary }
        });
        dialogRef.afterClosed().subscribe(function (result) {
            if (_this.destroyed || !result || !result.confirmed) {
                return;
            }
            _this.lastPatch = evaluation.patch;
            _this.lastSummary = evaluation.summary.slice();
            _this.sendPatch(evaluation.patch);
        });
    };
    AugerPurgeComponent.prototype.sendPatch = function (patch) {
        var _this = this;
        this.phase = 'sending';
        this.statusMessage = 'Sending…';
        this.formError = '';
        this.cdr.detectChanges();
        this.http.setConfig(patch).subscribe({
            next: function (result) {
                if (_this.destroyed) {
                    return;
                }
                if (result.status === 'acked') {
                    _this.phase = 'applied';
                    _this.statusMessage = AUGER_PURGE_MESSAGES.applied;
                    _this.loadConfig(true);
                    return;
                }
                if (result.status === 'failed') {
                    _this.phase = 'failed';
                    _this.statusMessage = AUGER_PURGE_MESSAGES.unreachable;
                    _this.cdr.detectChanges();
                    return;
                }
                _this.watchCommand(result.command_id);
            },
            error: function (error) {
                if (_this.destroyed) {
                    return;
                }
                _this.applyRequestError(error);
            }
        });
    };
    AugerPurgeComponent.prototype.watchCommand = function (commandId) {
        var _this = this;
        this.stopWatching();
        this.phase = 'waiting';
        this.statusMessage = AUGER_PURGE_MESSAGES.waiting;
        this.cdr.detectChanges();
        this.pollSubscription = interval(3000).pipe(takeUntil(timer(60000)), switchMap(function () { return _this.http.getCommand(commandId); }), takeWhile(function (command) { return isPendingAugerPurgeStatus(command.status); }, true)).subscribe({
            next: function (command) { return _this.onCommandUpdate(command); },
            error: function () {
                if (_this.destroyed) {
                    return;
                }
                _this.phase = 'failed';
                _this.statusMessage = 'Could not check whether the machine applied the change.';
                _this.cdr.detectChanges();
            },
            complete: function () {
                if (_this.destroyed || _this.phase !== 'waiting') {
                    return;
                }
                _this.phase = 'not_confirmed';
                _this.statusMessage = AUGER_PURGE_MESSAGES.notConfirmed;
                _this.cdr.detectChanges();
            }
        });
    };
    AugerPurgeComponent.prototype.onCommandUpdate = function (command) {
        if (this.destroyed || isPendingAugerPurgeStatus(command.status)) {
            return;
        }
        if (command.status === 'acked') {
            this.phase = 'applied';
            this.statusMessage = AUGER_PURGE_MESSAGES.applied;
            this.loadConfig(true);
            return;
        }
        this.phase = 'failed';
        this.statusMessage = command.error || AUGER_PURGE_MESSAGES.unreachable;
        this.cdr.detectChanges();
    };
    AugerPurgeComponent.prototype.loadProducts = function () {
        var _this = this;
        this.inventoryHttp.getMachineInventory({ machine_id: this.machineId }).subscribe({
            next: function (inventory) {
                if (_this.destroyed) {
                    return;
                }
                var products = {};
                var rows = Array.isArray(inventory) ? inventory : [];
                rows.forEach(function (row) {
                    var slot = Number(row && row.slotNo);
                    var name = row && row.name ? String(row.name).trim() : '';
                    if (!slot || !name || name.toUpperCase() === 'EMPTY') {
                        return;
                    }
                    products[slot] = mapApiProductNameToDisplayName(name);
                });
                _this.productsBySlot = products;
                _this.cdr.detectChanges();
            },
            error: function () {
                // The purge settings still load if inventory is unavailable.
            }
        });
    };
    AugerPurgeComponent.prototype.loadConfig = function (quiet) {
        var _this = this;
        if (!quiet) {
            this.isLoading = true;
            this.loadError = '';
        }
        this.http.getConfig(this.machineId).subscribe({
            next: function (config) {
                if (_this.destroyed) {
                    return;
                }
                _this.config = config;
                _this.draft = draftFromConfig(config);
                _this.serverFieldErrors = {};
                _this.formError = '';
                _this.isLoading = false;
                _this.cdr.detectChanges();
            },
            error: function (error) {
                if (_this.destroyed) {
                    return;
                }
                _this.isLoading = false;
                _this.loadError = error.message || 'Failed to load auger purge settings.';
                _this.cdr.detectChanges();
            }
        });
    };
    AugerPurgeComponent.prototype.applyRequestError = function (error) {
        var split = splitServerErrors(error.details, (this.lastPatch && this.lastPatch.canisters) || []);
        this.serverFieldErrors = split.fields;
        if (error.status === 400 && (split.general || Object.keys(split.fields).length)) {
            this.phase = 'idle';
            this.statusMessage = '';
            this.formError = split.general || 'Check the highlighted fields.';
        }
        else {
            this.phase = 'failed';
            this.statusMessage = error.message || AUGER_PURGE_MESSAGES.unreachable;
            this.formError = split.general;
        }
        this.cdr.detectChanges();
    };
    AugerPurgeComponent.prototype.clearServerErrors = function () {
        if (!Object.keys(this.serverFieldErrors).length && !this.formError) {
            return;
        }
        this.serverFieldErrors = {};
        this.formError = '';
    };
    AugerPurgeComponent.prototype.reportedInterval = function (value) {
        if (value === null || value === undefined) {
            return null;
        }
        var numeric = Number(value);
        return Number.isFinite(numeric) ? numeric : null;
    };
    AugerPurgeComponent.prototype.stopWatching = function () {
        if (this.pollSubscription) {
            this.pollSubscription.unsubscribe();
            this.pollSubscription = null;
        }
    };
    return AugerPurgeComponent;
}());
export { AugerPurgeComponent };
